import React, { useState } from 'react';
import { useAtlas } from '../../context/AtlasContext';
import {
    MapPinIcon,
    CaretDownIcon,
    CaretRightIcon,
    TrashIcon,
    PlusIcon
} from '@phosphor-icons/react';
import { Location } from '../../types';

interface AtlasSidebarProps {
    readOnly?: boolean;
}

export const AtlasSidebar: React.FC<AtlasSidebarProps> = ({ readOnly = false }) => {
    const {
        placedLocations,
        unplacedLocations,
        selectedLocationId,
        setSelectedLocationId,
        deleteLocation,
        setIsCreateModalOpen,
        setPendingPlacement
    } = useAtlas();

    const [placedExpanded, setPlacedExpanded] = useState(true);
    const [unplacedExpanded, setUnplacedExpanded] = useState(true);

    const handleDragStart = (e: React.DragEvent, location: Location) => {
        e.dataTransfer.setData('application/json', JSON.stringify({
            type: 'unplaced-location',
            locationId: location.id,
            name: location.name,
            description: location.description
        }));
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleCreateNew = () => {
        setPendingPlacement(null); // Will be set when dropped on canvas
        setIsCreateModalOpen(true);
    };

    return (
        <div className="absolute right-0 top-0 bottom-0 w-72 bg-zinc-900 border-l border-white/5 flex flex-col z-40">
            {/* Header */}
            <div className="p-4 border-b border-white/5 flex items-center justify-between">
                <h2 className="text-sm font-bold text-white">Locations</h2>
                {!readOnly && (
                    <button
                        onClick={handleCreateNew}
                        className="p-1.5 text-zinc-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                        title="Create new location"
                    >
                        <PlusIcon className="w-4 h-4" />
                    </button>
                )}
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
                {/* Placed Locations */}
                <div className="border-b border-white/5">
                    <button
                        onClick={() => setPlacedExpanded(!placedExpanded)}
                        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                    >
                        <span className="text-xs font-medium text-zinc-400 uppercase tracking-wider">
                            Placed ({placedLocations.length})
                        </span>
                        {placedExpanded ? (
                            <CaretDownIcon className="w-4 h-4 text-zinc-500" />
                        ) : (
                            <CaretRightIcon className="w-4 h-4 text-zinc-500" />
                        )}
                    </button>

                    {placedExpanded && (
                        <div className="px-2 pb-2 space-y-1">
                            {placedLocations.length === 0 ? (
                                <p className="px-2 py-3 text-xs text-zinc-600 text-center">
                                    No locations placed yet
                                </p>
                            ) : (
                                placedLocations.map((location) => (
                                    <LocationItem
                                        key={location.id}
                                        location={location}
                                        isSelected={selectedLocationId === location.id}
                                        onSelect={() => setSelectedLocationId(location.id)}
                                        onDelete={() => deleteLocation(location.id)}
                                        isPlaced={true}
                                    />
                                ))
                            )}
                        </div>
                    )}
                </div>

                {/* Unplaced Locations */}
                <div>
                    <button
                        onClick={() => setUnplacedExpanded(!unplacedExpanded)}
                        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                    >
                        <span className="text-xs font-medium text-zinc-400 uppercase tracking-wider">
                            Unplaced ({unplacedLocations.length})
                        </span>
                        {unplacedExpanded ? (
                            <CaretDownIcon className="w-4 h-4 text-zinc-500" />
                        ) : (
                            <CaretRightIcon className="w-4 h-4 text-zinc-500" />
                        )}
                    </button>

                    {unplacedExpanded && (
                        <div className="px-2 pb-2 space-y-1">
                            {unplacedLocations.length === 0 ? (
                                <p className="px-2 py-3 text-xs text-zinc-600 text-center">
                                    No unplaced locations
                                </p>
                            ) : (
                                unplacedLocations.map((location) => (
                                    <LocationItem
                                        key={location.id}
                                        location={location}
                                        isSelected={selectedLocationId === location.id}
                                        onSelect={() => setSelectedLocationId(location.id)}
                                        onDelete={() => deleteLocation(location.id)}
                                        isPlaced={false}
                                        onDragStart={(e) => handleDragStart(e, location)}
                                    />
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Help Text */}
            <div className="p-4 border-t border-white/5">
                <p className="text-xs text-zinc-600">
                    Drag unplaced locations to the canvas to place them and generate map images.
                </p>
            </div>
        </div>
    );
};

// Location list item component
interface LocationItemProps {
    location: Location;
    isSelected: boolean;
    onSelect: () => void;
    onDelete: () => void;
    isPlaced: boolean;
    onDragStart?: (e: React.DragEvent) => void;
}

const LocationItem: React.FC<LocationItemProps> = ({
    location,
    isSelected,
    onSelect,
    onDelete,
    isPlaced,
    onDragStart
}) => {
    const [showDelete, setShowDelete] = useState(false);

    return (
        <div
            className={`group relative flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                isSelected
                    ? 'bg-indigo-500/20 border border-indigo-500/50'
                    : 'hover:bg-white/5 border border-transparent'
            }`}
            onClick={onSelect}
            onMouseEnter={() => setShowDelete(true)}
            onMouseLeave={() => setShowDelete(false)}
            draggable={!isPlaced}
            onDragStart={onDragStart}
        >
            {/* Thumbnail or Icon */}
            {isPlaced && location.atlas_image_url ? (
                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-zinc-800">
                    <img
                        src={location.atlas_image_url}
                        alt={location.name}
                        className="w-full h-full object-cover"
                    />
                </div>
            ) : (
                <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                    <MapPinIcon className="w-5 h-5 text-zinc-500" weight="duotone" />
                </div>
            )}

            {/* Info */}
            <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{location.name}</p>
                {location.description && (
                    <p className="text-xs text-zinc-500 truncate">{location.description}</p>
                )}
            </div>

            {/* Delete Button */}
            {showDelete && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        if (confirm(`Delete "${location.name}"?`)) {
                            onDelete();
                        }
                    }}
                    className="absolute right-2 p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                    <TrashIcon className="w-4 h-4" />
                </button>
            )}
        </div>
    );
};
